(() => {
  const root = document.getElementById('NativeCartDemo');
  if (!root) return;

  const routesRoot = window.Shopify?.routes?.root || '/';
  const mainVariant = Number(root.dataset.mainVariant || 0);
  const bumpVariant = Number(root.dataset.bumpVariant || 0);
  const mainRegular = Number(root.dataset.mainRegular || 9700);
  const bumpRegular = Number(root.dataset.bumpRegular || 1800);
  const mainImage = root.dataset.mainImage || '';
  const lines = root.querySelector('[data-nc-lines]');
  const total = root.querySelector('[data-nc-total]');
  const notice = root.querySelector('.nc-notice');
  const bump = root.querySelector('[data-nc-bump]');
  const bumpToggle = root.querySelector('[data-nc-bump-toggle]');
  const savingsBox = root.querySelector('[data-nc-savings]');
  const savingsCopy = root.querySelector('[data-nc-savings-copy]');
  const checkout = root.querySelector('[data-nc-checkout]');
  const modal = root.querySelector('[data-nc-modal]');
  const modalContent = root.querySelector('[data-nc-detail-content]');
  let cart = null;
  let busy = false;

  const money = cents => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format((cents || 0) / 100);
  const escapeHtml = value => String(value || '').replace(/[&<>'"]/g, char => ({ '&':'&amp;', '<':'&lt;', '>':'&gt;', "'":'&#39;', '"':'&quot;' }[char]));
  const showNotice = message => {
    notice.textContent = message;
    notice.hidden = !message;
  };

  async function request(path, options) {
    const response = await fetch(routesRoot + path, options);
    if (!response.ok) throw new Error('Cart update failed. Please try again.');
    return response.json();
  }

  async function getCart() {
    cart = await request('cart.js', { headers: { Accept: 'application/json' } });
    render();
    return cart;
  }

  function productKind(item) {
    if (Number(item.variant_id) === mainVariant) return 'main';
    if (Number(item.variant_id) === bumpVariant) return 'bump';
    return '';
  }

  function render() {
    if (!cart) return;
    // The bump is presented exclusively in the dedicated add-on card below.
    // It remains in the Shopify cart when selected, so totals and checkout stay accurate.
    lines.innerHTML = cart.items.filter(item => Number(item.variant_id) !== bumpVariant).map(item => {
      const kind = productKind(item);
      const detail = kind ? `data-nc-details="${kind}"` : '';
      const preferredImage = kind === 'main' && mainImage ? mainImage : item.image;
      const image = preferredImage ? `<img src="${escapeHtml(preferredImage)}" alt="${escapeHtml(item.product_title)}">` : '<span class="nc-placeholder">PDF</span>';
      const regular = kind === 'main' ? mainRegular : kind === 'bump' ? bumpRegular : item.final_price;
      const saved = Math.max(0, regular - item.final_price);
      const removeButton = kind ? '' : `<button class="nc-remove" type="button" data-nc-remove="${escapeHtml(item.key)}">Remove</button>`;
      const promo = saved > 0 ? `<div class="nc-item-promo"><span class="nc-ticket">WELCOME</span><b>Save ${money(saved)}</b></div>` : '';
      const offerPrice = saved > 0 ? `<div class="nc-line-offer"><s>${money(regular)}</s><strong>${money(item.final_price)}</strong></div>` : `<strong class="nc-line-price">${money(item.final_price)}</strong>`;
      return `<article class="nc-line" data-line-key="${escapeHtml(item.key)}">
        <button class="nc-line-media" type="button" ${detail} ${detail ? '' : 'disabled'}>${image}</button>
        <div>
          <button class="nc-line-title" type="button" ${detail} ${detail ? '' : 'disabled'}><h3>${escapeHtml(item.product_title)}</h3></button>
          <div class="nc-line-actions"><span>Digital access · Qty 1</span>${removeButton}</div>${promo}
        </div>
        ${offerPrice}
      </article>`;
    }).join('') || '<p>Your cart is empty.</p>';

    total.textContent = money(cart.total_price);
    checkout.disabled = cart.item_count === 0;
    const hasBump = cart.items.some(item => Number(item.variant_id) === bumpVariant);
    if (bumpToggle) bumpToggle.checked = hasBump;
    if (bump) bump.classList.toggle('nc-added', hasBump);
    const savings = cart.items.reduce((sum, item) => {
      if (Number(item.variant_id) === mainVariant) return sum + Math.max(0, mainRegular - item.final_price) * item.quantity;
      if (Number(item.variant_id) === bumpVariant) return sum + Math.max(0, bumpRegular - item.final_price) * item.quantity;
      return sum;
    }, 0);
    if (savingsBox) savingsBox.hidden = savings <= 0;
    if (savingsCopy) savingsCopy.textContent = `You save ${money(savings)} today`;
  }

  async function addVariant(id) {
    if (!id) throw new Error('This product is not available in the demo yet.');
    return request('cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id, quantity: 1 }] })
    });
  }

  async function removeKey(key) {
    return request('cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 })
    });
  }

  async function setKeyQuantity(key, quantity) {
    return request('cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity })
    });
  }

  async function normalizeDigitalQuantities() {
    if (!cart) return;
    const duplicate = cart.items.find(item => productKind(item) && item.quantity !== 1);
    if (duplicate) {
      await setKeyQuantity(duplicate.key, 1);
      await getCart();
      return normalizeDigitalQuantities();
    }
  }

  async function transact(action) {
    if (busy) return;
    busy = true;
    root.classList.add('nc-loading');
    showNotice('');
    try {
      await action();
      await getCart();
      await normalizeDigitalQuantities();
    } catch (error) {
      showNotice(error.message || 'Something went wrong. Please try again.');
      await getCart().catch(() => {});
    } finally {
      busy = false;
      root.classList.remove('nc-loading');
    }
  }

  async function openCart(addMain = false) {
    root.classList.add('nc-open');
    root.setAttribute('aria-hidden', 'false');
    document.documentElement.style.overflow = 'hidden';
    await getCart();
    await normalizeDigitalQuantities();
    if (addMain && mainVariant && !cart.items.some(item => Number(item.variant_id) === mainVariant)) {
      await transact(() => addVariant(mainVariant));
    }
    root.querySelector('.nc-drawer').focus();
  }

  function closeCart() {
    root.classList.remove('nc-open');
    root.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = '';
  }

  function showDetails(kind) {
    const template = root.querySelector(`[data-nc-${kind}-detail]`);
    if (!template) return;
    modalContent.replaceChildren(template.content.cloneNode(true));
    modal.hidden = false;
    modal.querySelector('.nc-modal-card').focus?.();
  }

  function closeDetails() { modal.hidden = true; }

  document.addEventListener('click', event => {
    const cta = event.target.closest('.hero-cta,.contents-cta,.reviews-cta,.bundle-cta,.guarantee-cta,.faq-cta,.final-cta,.mobile-buy a');
    if (cta) {
      event.preventDefault();
      event.stopImmediatePropagation();
      openCart(true);
      return;
    }
    if (event.target.closest('[data-nc-close]')) closeCart();
  }, true);

  root.addEventListener('click', event => {
    const details = event.target.closest('[data-nc-details]');
    if (details) showDetails(details.dataset.ncDetails);

    const remove = event.target.closest('[data-nc-remove]');
    if (remove) transact(() => removeKey(remove.dataset.ncRemove));

    if (event.target.closest('[data-nc-modal-close]')) closeDetails();

    if (event.target.closest('[data-nc-modal-add]')) {
      transact(async () => {
        if (!cart) await getCart();
        if (!cart.items.some(item => Number(item.variant_id) === bumpVariant)) await addVariant(bumpVariant);
        closeDetails();
      });
    }
  });

  bumpToggle?.addEventListener('change', () => {
    transact(async () => {
      if (!cart) await getCart();
      const line = cart.items.find(item => Number(item.variant_id) === bumpVariant);
      if (bumpToggle.checked && !line) await addVariant(bumpVariant);
      if (!bumpToggle.checked && line) await removeKey(line.key);
    });
  });

  checkout.addEventListener('click', () => {
    if (cart?.item_count) window.location.assign(routesRoot + 'checkout');
  });

  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;
    if (!modal.hidden) closeDetails(); else closeCart();
  });
})();
